import { useCallback, useContext, useRef } from 'react';
import uniqueId from 'lodash/uniqueId';
import { useClientOnlyLayoutEffect } from 'hooks/use-client-only-layout-effect';

import { KEY_DOWN, KEY_ENTER, KEY_LEFT, KEY_RIGHT, KEY_UP } from 'constants/keys';

import { NavigationContext } from './NavigationProvider';

export const useNavigation = (ref, { id, skip } = {}) => {
  const { current: uid } = useRef(uniqueId('navitem_'));
  const {
    focus,
    focusNext,
    focusPrevious,
    handleLeft,
    handleRight,
    handleEnter,
    register,
    selectedUid,
    unregister,
  } = useContext(NavigationContext);

  const outsideProvider = register === null;

  useClientOnlyLayoutEffect(() => {
    if (outsideProvider) return undefined;
    if (ref.current === undefined) return undefined;
    if (!skip) register({ id, ref, uid });
    return () => unregister(uid);
    // eslint-disable-next-line react-hooks/refs
  }, [id, outsideProvider, ref, register, skip, uid, unregister]);

  const handleKeyDown = useCallback(
    event => {
      const { keyCode } = event;
      const accessKeys = [KEY_DOWN, KEY_ENTER, KEY_LEFT, KEY_RIGHT, KEY_UP];

      if (!accessKeys.includes(keyCode)) return;

      if (keyCode === KEY_DOWN) focusNext(uid);
      else if (keyCode === KEY_UP) focusPrevious(uid);
      else if (keyCode === KEY_RIGHT) handleRight(uid);
      else if (keyCode === KEY_LEFT) handleLeft(uid);
      else if (keyCode === KEY_ENTER) {
        handleEnter(uid);
        return; // let the event with the default behavior
      }

      event.preventDefault();
    },
    [focusNext, focusPrevious, handleEnter, handleLeft, handleRight, uid],
  );

  const handleClick = useCallback(() => {
    focus(uid);
  }, [focus, uid]);

  // eslint-disable-next-line react-hooks/refs
  const selected = uid === selectedUid;

  return outsideProvider
    ? {
        selected: false,
        tabIndex: '0',
        handleClick: () => {},
        handleKeyDown: () => {},
      }
    : {
        selected,
        tabIndex: selected ? '0' : '-1',
        handleClick,
        handleKeyDown,
      };
};
